import { Expand01, Expand06 } from '@timelyapp/icons/line-icons';
import React, { Suspense, useState } from 'react';
import { Modal } from '../_radix-ui/modal/Modal';
import Button from '../Button';
import styles from './JsonView.module.scss';

// Lazy load the JsonView component from @uiw/react-json-view
const JsonViewComponent = React.lazy(() => import('@uiw/react-json-view'));

const JsonContent = ({ data }: JsonViewProps) => {
  const jsonValue = typeof data === 'object' && data !== null ? data : { value: data };
  return (
    <Suspense fallback={<code>{JSON.stringify(data, null, 2)}</code>}>
      <JsonViewComponent value={jsonValue} />
    </Suspense>
  );
};

export interface JsonViewProps {
  /** The data to display in the JSON viewer */
  data: unknown;
  /** Maximum height before scrolling. Defaults to '300px' */
  maxHeight?: number;
  /** Renders as a single debug button that opens fullscreen view */
  collapsed?: boolean;
  /** Optional title for the fullscreen modal */
  title?: string;
}

export const JsonView = React.memo<JsonViewProps>(
  ({ data, maxHeight = '300px', collapsed = true, title = 'Debug JSON View' }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const containClicks = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const renderModal = showModal ? (
      <Modal open={showModal} onOpenChange={setShowModal} title={title} variant="overlayView">
        <div onClick={containClicks}>
          <JsonContent data={data} />
        </div>
      </Modal>
    ) : null;

    const renderExpandBtn = (
      <Button
        size="xsmall"
        tooltip={isExpanded ? 'Collapse' : 'Expand'}
        onClick={e => {
          containClicks(e);
          setIsExpanded(!isExpanded);
        }}
        selected={isExpanded}
      >
        <Expand01 />
      </Button>
    );

    const renderFullscreenBtn = (
      <Button
        size="xsmall"
        onClick={e => {
          containClicks(e);
          setShowModal(true);
        }}
        tooltip="Open&nbsp;JsonView&nbsp;Modal"
      >
        <Expand06 />
      </Button>
    );

    // Simple button to open modal
    if (collapsed) {
      return (
        <>
          <span>{renderFullscreenBtn}</span>
          {renderModal}
        </>
      );
    }

    // Inline view with expand/modal options
    return (
      <>
        <div
          className={styles.inlineContainer}
          style={{ maxHeight: isExpanded ? '100%' : maxHeight }}
        >
          <div className={styles.floatingActions}>
            {renderExpandBtn}
            {renderFullscreenBtn}
          </div>
          <JsonContent data={data} />
        </div>
        {renderModal}
      </>
    );
  }
);

JsonView.displayName = 'JsonView';
